import { ref } from 'vue';
import type { Router } from 'vue-router';
import { forgetWorkspaceLocalStateForOwner } from './workspaceLocalState';

// Workspace reachability (issue 364, F57). Every module API's tenant middleware
// answers BOTH a suspended workspace and a slug that never existed with the same
// `404 TENANT_NOT_FOUND` — each service resolves a non-active tenant to "not
// found" on purpose (ats-api's `getTenantBySlugWithModules` returns null; the
// others answer `tenant_not_found` for any `status !== 'active'`). That merge is
// the right shape and must survive: a page that told the two apart would confirm
// which workspace slugs exist to anyone who can type a URL.
//
// Without the switch pinned here, no UI handles that code at all: every read on
// the screen 404s and each app draws whatever empty state it happens to own —
// dead tiles, "record not found" toasts, an app shell around nothing. That is
// the failure this file exists to prevent, and it is the staff-side twin of
// `portal-ui/src/portal-availability.ts` (issue 221, D26), whose discipline is
// copied here deliberately.
//
// The seam lives in app-kit rather than in each app because that is what app-kit
// is for: `apiError` already sits next to it, so no UI can be forgotten.

/** The ONE code that may black out the app. See `noteTenantUnavailability`. */
export const TENANT_NOT_FOUND_CODE = 'TENANT_NOT_FOUND';

/**
 * The code user-api's member routes (the onboarding status, the demo reset) and
 * ats-api's tenant middleware answer a signed-in caller who holds NO membership
 * of the workspace — never had one, or just lost it (a demo guest revoked, the
 * demo ended and wiped its members). 403 with this code is "you are no longer in
 * here", which no retry fixes (issue 669).
 */
export const TENANT_MEMBERSHIP_DENIED_CODE = 'TENANT_MEMBERSHIP_DENIED';

/** Raised once the workspace is known to be unreachable; the app root reads it. */
export const tenantUnavailable = ref(false);

/**
 * Raised together with `tenantUnavailable` when the cause is the person's own
 * lost membership rather than the workspace: the page then says "this demo has
 * ended or your access was removed" instead of the one neutral sentence. Safe to
 * say — the caller IS signed in and was told 403 for exactly this fact; naming
 * it confirms nothing about which workspaces exist.
 */
export const tenantAccessEnded = ref(false);

// A route that legitimately works WITHOUT a resolved tenant (the workspace's own
// login/password-reset/invite doors, an error page) must survive the blackout —
// blacking one out would take away the very page a person can still use. Set
// from the router (`meta.tenantless`, via `installTenantlessRouteFlag`) so the
// list of exempt pages lives at the route definitions, not in a second copy
// here. Same mechanism portal-ui uses for the candidate's rights routes.
export const onTenantlessRoute = ref(false);

export function markTenantUnavailable(): void {
  if (onTenantlessRoute.value) return;
  tenantUnavailable.value = true;
}

/**
 * Who answered a `TENANT_MEMBERSHIP_DENIED`. user-api is the authority on
 * membership: its own onboarding routes (the status, the reset, the journeys) are
 * `'user-api'` and their denial is believed at once. A module API's tenant
 * middleware (`'module'`, the default — what an axios interceptor sees) keeps its
 * own short cache that also stores negative answers, so its denial can outlive a
 * re-invitation; it only asks user-api again (see `registerMembershipConfirmer`).
 */
export type MembershipDenialSource = 'user-api' | 'module';

/**
 * What a `'module'` denial triggers: ask user-api whether the signed-in person is
 * a member now, and call `raiseTenantAccessEnded` only when it says no. Registered
 * by the onboarding store, which owns the user-api client (this util imports none).
 * It must RETURN once it has decided; a failure raises nothing.
 */
export type MembershipConfirmer = () => Promise<void>;

let membershipConfirmer: MembershipConfirmer | null = null;
let confirmingMembership = false;

export function registerMembershipConfirmer(confirmer: MembershipConfirmer | null): void {
  membershipConfirmer = confirmer;
}

// One confirmation in flight per app: a screen firing six module calls that are
// all denied asks user-api once, not six times.
function confirmMembership(): void {
  if (!membershipConfirmer || confirmingMembership) return;
  confirmingMembership = true;
  let pending: Promise<void>;
  try {
    pending = Promise.resolve(membershipConfirmer());
  } catch {
    pending = Promise.resolve();
  }
  void pending
    .catch(() => undefined)
    .finally(() => {
      confirmingMembership = false;
    });
}

/**
 * The response-interceptor hook. Raises the flag on `TENANT_NOT_FOUND` and on
 * NOTHING else — a plain 404 still means "this record is gone", which is a
 * per-page state, and taking the whole app down on it would hide a perfectly
 * live workspace because one application was deleted.
 *
 * Returns true when the failure WAS a tenant-unavailable one, so a caller that
 * wants to skip its own error surfacing can. Callers must still reject: this
 * only observes. `tenantSlug` and `userId` are for a caller that knows the
 * workspace and the account the call was for (the onboarding stores) — the
 * lost-membership branch clears that account's local state for that workspace;
 * without them the request's own `X-Tenant-Slug` and bearer token are read.
 *
 * `TENANT_MEMBERSHIP_DENIED` raises the ended page at once only when user-api
 * itself said it (`source: 'user-api'`); from a module API it triggers a
 * confirmation against user-api and raises nothing by itself.
 */
export function noteTenantUnavailability(
  error: unknown,
  tenantSlug?: string | null,
  userId?: string | null,
  source: MembershipDenialSource = 'module',
): boolean {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  const code = data && typeof data === 'object' ? (data as { code?: unknown }).code : undefined;
  if (code === TENANT_MEMBERSHIP_DENIED_CODE) {
    if (source === 'user-api') noteAccessEnded(error, tenantSlug, userId);
    else confirmMembership();
    return true;
  }
  if (code !== TENANT_NOT_FOUND_CODE) return false;
  markTenantUnavailable();
  return true;
}

// The tenant slug the failed request was addressed to — every module client stamps
// it as `X-Tenant-Slug`, on AxiosHeaders or a plain object alike.
function requestTenantSlug(error: unknown): string | null {
  const headers = (error as { config?: { headers?: unknown } } | null)?.config?.headers as
    | { get?: (name: string) => unknown; [key: string]: unknown }
    | undefined;
  if (!headers) return null;
  const raw = (typeof headers.get === 'function' ? headers.get('X-Tenant-Slug') : undefined) ?? headers['X-Tenant-Slug'] ?? headers['x-tenant-slug'];
  const slug = typeof raw === 'string' ? raw.trim() : '';
  return slug || null;
}

// The account the failed request was made AS: the `userId` claim of its bearer
// token. The interceptors that call the seam know the request, not the session.
function requestUserId(error: unknown): string | null {
  const headers = (error as { config?: { headers?: unknown } } | null)?.config?.headers as
    | { get?: (name: string) => unknown; [key: string]: unknown }
    | undefined;
  if (!headers) return null;
  const raw = (typeof headers.get === 'function' ? headers.get('Authorization') : undefined) ?? headers.Authorization ?? headers.authorization;
  const token = typeof raw === 'string' ? raw.replace(/^Bearer\s+/i, '').trim() : '';
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json?.userId === 'string' && json.userId ? json.userId : null;
  } catch {
    return null;
  }
}

/**
 * The person's membership of this workspace is gone (a revoked demo guest, the
 * demo ended under an open tab, or a workspace this account is not listed in).
 * Everything THIS ACCOUNT's browser state remembers about the workspace — the
 * active role, delegation, the demo persona, the first-visit card, drafts — goes
 * now, so no later visit puts a stale header on the wire; then the app root swaps
 * to the ended page. Only this account's entries: the workspace is alive and
 * another account in this browser may still belong to it. Nothing reloads, so
 * nothing can loop.
 */
export function raiseTenantAccessEnded(tenantSlug: string | null | undefined, userId: string | null | undefined): void {
  if (onTenantlessRoute.value) return;
  forgetWorkspaceLocalStateForOwner(tenantSlug, userId);
  tenantAccessEnded.value = true;
  markTenantUnavailable();
}

function noteAccessEnded(error: unknown, tenantSlug?: string | null, userId?: string | null): void {
  raiseTenantAccessEnded(tenantSlug || requestTenantSlug(error), userId || requestUserId(error));
}

/**
 * Drive `onTenantlessRoute` from the app's own route definitions.
 *
 * beforeEach, not afterEach: a view's fetches run once it is mounted, and the
 * flag has to be right before the first of them can fail.
 */
export function installTenantlessRouteFlag(router: Router): void {
  router.beforeEach((to) => {
    onTenantlessRoute.value = to.meta?.tenantless === true;
    return true;
  });
}

/**
 * `account.{LUMI_DOMAIN}` — the data subject's self-service host (ARCHITECTURE.md):
 * tenant-less on purpose, so it is the one address that still works when the
 * workspace does not, and it carries the Art. 15(3) takeout, the Art. 17 erasure
 * request AND the invoice list a suspended workspace still owes its owner.
 *
 * DERIVED FROM THE CURRENT HOST, never hard-coded: `{slug}.{domain}` →
 * `account.{domain}`. app-kit owns no runtime config
 * (see LegalLinksFooter), so an app that HAS resolved `LUMI_DOMAIN` passes its
 * own value into the view instead and this is only the fallback. Empty when
 * there is no domain to point at — the view then renders no link rather than a
 * link into the void, the same posture the legal footer takes.
 */
export function accountBaseUrlFromHost(): string {
  if (typeof window === 'undefined') return '';
  const host = String(window.location.hostname || '').toLowerCase();
  if (!host || host === 'localhost' || host.endsWith('.localhost')) return '';
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return '';
  const labels = host.split('.');
  if (labels.length < 3) return '';
  return `${window.location.protocol}//account.${labels.slice(1).join('.')}`;
}
